import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import parse from 'html-react-parser'
import { fetchEncryptedContent, getCompany } from '../../services/ApiService'
import moment from 'moment'
import { generateTermsPDF, getSignedS3Url, isRunningInIframe } from '../../lib/Global'
import { useDispatch, useSelector } from 'react-redux'
import { loadingActions } from '../../store/loading'
import md5 from 'md5'
import { sseClientKeys } from '../../lib/Constants'

const useStyles = makeStyles(theme => ({
  infoContainer: {
    paddingTop: '24px'
  },
  titleContainer: {
    padding: '24px 80px 0px 80px',
    columnCount: '2',
    [theme.breakpoints.down('md')]: {
      columnCount: '1',
      padding: '24px 0 0 0'
    }
  },
  itemContainer: {
    margin: '10px 80px 0px 80px',
    height: '55vh',
    overflowY: 'scroll',
    overflowX: 'auto',
    marginTop: '15px',
    scrollBehavior: 'smooth',
    [theme.breakpoints.down('md')]: {
      margin: '10px 10px 0px 10px',
      minWidth: '320px'
    }
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: theme.colors.black,
    lineHeight: '24px',
    letterSpacing: '0.05em'
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: '300',
    lineHeight: '17px',
    color: theme.colors.black,
    paddingTop: '9px',
    letterSpacing: '0em'
  },
  downloadButton: {
    background: theme.colors.grey_2,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: '400',
    borderRadius: '36px',
    width: '160px',
    alignSelf: 'flex-end',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignSelf: 'center',
      marginRight: 0
    },
    '&.MuiButton-contained': {
      fontSize: '12px',
      width: '81px'
    }
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'start'
  }
}))

export const TermsComponent = props => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { content, originatingCompany, data } = props
  const dispatch = useDispatch()
  const [processedContent, setProcessedContent] = useState('')
  const userStore = useSelector(state => state.auth.user)
  const contentBoxRef = useRef(null)

  const termsSignature = userStore.userInfo?.configurations?.portals?.subcontractor?.onboarding?.compliance?.terms?.mandatory_signature

  const smoothScrollToBottom = () => {
    if (contentBoxRef.current) {
      contentBoxRef.current.scrollTo({
        top: contentBoxRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    setProcessedContent(content)

    const decryptSignature = async () => {
      dispatch(loadingActions.show())

      try {
        const clientId = isRunningInIframe() ? userStore?.userInfo?.subdomain?.child : userStore?.userInfo?.parent_subdomain
        const signedUrl = await getSignedS3Url(data.signature_url, '', 'getObject')
        dispatch(loadingActions.hide())

        // Set up decryption headers
        const sseKey = Buffer.alloc(32, sseClientKeys[clientId])
        const encodedSseKey = encodeURI(sseKey.toString('base64'))
        const md5Hash = Buffer.from(encodeURI(md5(sseKey)), 'hex').toString('base64')

        // Fetch with decryption headers
        const response = await fetchEncryptedContent(signedUrl, encodedSseKey, md5Hash)

        if (!response.ok) {
          throw new Error(`Failed to fetch signature: ${response.status}`)
        }

        const blob = await response.blob()

        return new Promise((resolve, reject) => {
          const reader = new FileReader()

          reader.onloadend = () => {
            const dataUrl = reader.result
            resolve(dataUrl)
          }

          reader.onerror = (error) => {
            reject(error)
          }

          reader.readAsDataURL(blob)
        })
      } catch (error) {
      /* eslint-disable no-console */
        console.error('Error decrypting signature:', error)
        dispatch(loadingActions.hide())
        return null
      }
    }

    const processSignatureData = async () => {
      // If signature data is available, inject it into the content
        try {
          const dataUrl = await decryptSignature()
          if (dataUrl) {
            // Process data attributes used in the HTML template
            const parser = new DOMParser()
            const doc = parser.parseFromString(content, 'text/html')

            // Update name fields
            doc.querySelectorAll('[data-signature-field="trade-partner-name"]')
              .forEach(el => {
                el.textContent = data.signer_name || ''
              })

            // Update signature fields
            const signatureImageStyle = 'max-width:260px;max-height:65px;margin-bottom:-15px;'
            doc.querySelectorAll('[data-signature-field="trade-partner-signature"]')
              .forEach(el => {
                el.innerHTML = `<img src="${dataUrl}" alt="Signature" style="${signatureImageStyle}"/>`
              })

            // Update title fields
            doc.querySelectorAll('[data-signature-field="trade-partner-title"]')
              .forEach(el => {
                el.textContent = `${data.signer_title || ''}`
              })

            // Update date fields
            doc.querySelectorAll('[data-signature-field="trade-partner-date"]')
            .forEach(el => {
              el.textContent = `${moment(new Date(data.signed_on)).format('MM/DD/YYYY')}`
            })

            setProcessedContent(doc.body.innerHTML)
            setTimeout(smoothScrollToBottom, 300)
          }
        } catch (error) {
          /* eslint-disable no-console */
          console.error('Error processing content:', error)
        } finally {
          dispatch(loadingActions.hide())
        }
    }

    if (termsSignature && content && data && data.signature_url) {
      processSignatureData()
    }
  }, [content, data])

  async function generatePDF() {
    dispatch(loadingActions.show())
    const company = await getCompany(originatingCompany)
    const signDate = moment(new Date(data?.signed_on)).format(
      t('general.format.short_date')
    )
    const signTime = moment(new Date(data?.signed_on)).format(
      t('general.format.time_am_pm')
    )
    generateTermsPDF(
      company.name,
      termsSignature ? processedContent : content,
      data?.signed,
      termsSignature ? data?.signer_name : data?.name,
      signDate,
      signTime,
      t
    )
    dispatch(loadingActions.hide())
  }

  const displayContent = termsSignature ? processedContent : content

  return (
    <Container classes={{ root: classes.infoContainer }}>
      <Grid container className={classes.titleContainer}>
        <Grid item xs={10}>
          <Typography classes={{ root: classes.title }}>
            {t('company_profile.terms')}
          </Typography>
          <Typography classes={{ root: classes.subtitle }}>
            {t('company_profile.message.terms')}
          </Typography>
        </Grid>
        <Grid item xs={2} className={classes.buttonContainer}>
          <Button
            variant="contained"
            className={classes.downloadButton}
            onClick={generatePDF}
          >
            {t('company_profile.export_pdf')}
          </Button>
        </Grid>
      </Grid>
      <Box
        display="flex"
        className={classes.itemContainer}
        id="content-to-export"
        ref={contentBoxRef}
      >
        {parse(displayContent)}
      </Box>
    </Container>
  )
}
